// external imports
import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { Add } from '@mui/icons-material';
import { toast } from 'react-toastify';

// internal imports
import Layout from 'Component/Layouts/layout';
import useApiCaller from 'hooks/apiCaller';
import LanguageConfigTable from './LanguageConfigTable';
import LanguageConfigModal from './LanguageConfigModal';

const parseApiError = (err, fallback) => {
  const status = err?.response?.status;
  const message = err?.response?.data?.message;
  if (status === 401) {
    return {
      status,
      message: message || 'You are not authorized to manage language configs.',
      unauthorized: true,
    };
  }
  return { status, message: message || fallback, unauthorized: false };
};

const LanguageConfig = () => {
  const apiCaller = useApiCaller();

  // data
  const [configs, setConfigs] = React.useState([]);
  const [languages, setLanguages] = React.useState([]);

  // table load state
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [unauthorized, setUnauthorized] = React.useState(false);

  // per-row toggle/delete in-flight ids (so we can disable individual controls)
  const [busyId, setBusyId] = React.useState(null);

  // modal control — editingConfig is null for Add, the row object for Edit
  const [modalOpen, setModalOpen] = React.useState(false);
  const [editingConfig, setEditingConfig] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [formError, setFormError] = React.useState(null);

  // GET /task/langconfig — reused after every mutation
  const fetchConfigs = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setUnauthorized(false);
      const response = await apiCaller.get('/task/langconfig');
      setConfigs(response?.data?.data?.configs ?? []);
    } catch (err) {
      const parsed = parseApiError(err, 'Failed to load language configs. Please try again.');
      setError(parsed.message);
      setUnauthorized(parsed.unauthorized);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // GET /task/supported-languages — populates the Add form dropdown
  const fetchLanguages = React.useCallback(async () => {
    try {
      const response = await apiCaller.get('/task/supported-languages');
      setLanguages(response?.data?.data?.languages ?? []);
    } catch (err) {
      // non-fatal: the table still renders; the Add form just has no options
      console.error('Error fetching supported languages:', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    fetchConfigs();
    fetchLanguages();
  }, [fetchConfigs, fetchLanguages]);

  // ---- modal open helpers ----
  const openAdd = () => {
    setEditingConfig(null);
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = (config) => {
    setEditingConfig(config);
    setFormError(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    if (submitting) return;
    setModalOpen(false);
    setEditingConfig(null);
    setFormError(null);
  };

  // POST /task/langconfig — upsert (handles both Add and Edit)
  const handleSubmit = async ({ languageCode, translatorEmpId }) => {
    try {
      setSubmitting(true);
      setFormError(null);
      await apiCaller.post('/task/langconfig', {
        languageCode,
        translatorEmpIds: [Number(translatorEmpId)],
      });
      toast.success(editingConfig ? 'Language config updated.' : 'Language config added.');
      setModalOpen(false);
      setEditingConfig(null);
      await fetchConfigs();
    } catch (err) {
      const parsed = parseApiError(err, 'Failed to save language config. Please try again.');
      // surface inline in the form (esp. 400 unsupported code / 404 employee not found)
      setFormError(parsed.message);
      if (parsed.unauthorized) toast.error(parsed.message);
    } finally {
      setSubmitting(false);
    }
  };

  // PUT /task/langconfig/:id/toggle — flip isActive
  const handleToggle = async (config) => {
    try {
      setBusyId(config._id);
      await apiCaller.put(`/task/langconfig/${config._id}/toggle`);
      toast.success(`${config.languageName} ${config.isActive ? 'deactivated' : 'activated'}.`);
      await fetchConfigs();
    } catch (err) {
      const parsed = parseApiError(err, 'Failed to update status. Please try again.');
      toast.error(parsed.message);
    } finally {
      setBusyId(null);
    }
  };

  // DELETE /task/langconfig/:id
  const handleDelete = async (config) => {
    try {
      setBusyId(config._id);
      await apiCaller.delete(`/task/langconfig/${config._id}`);
      toast.success(`${config.languageName} config deleted.`);
      await fetchConfigs();
    } catch (err) {
      const parsed = parseApiError(err, 'Failed to delete config. Please try again.');
      toast.error(parsed.message);
    } finally {
      setBusyId(null);
    }
  };

  // languageCodes already configured — disabled in the Add dropdown
  const usedLanguageCodes = React.useMemo(() => configs.map((c) => c.languageCode), [configs]);

  return (
    <Layout>
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          p: { xs: 2, md: 3 },
        }}
      >
        {/* Header card */}
        <Box
          sx={{
            mb: 2,
            background: '#ffffff',
            borderRadius: '12px',
            p: { xs: 2, md: 3 },
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight="700" sx={{ color: '#1e293b', mb: 0.5 }}>
              Language → Translator Routing
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b' }}>
              Every new task auto-assigns each target language to the translator configured here.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={openAdd}
            disabled={unauthorized}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '10px',
              px: 2.5,
              py: 1.1,
              background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
              boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)',
              '&:hover': { background: 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)' },
            }}
          >
            Add Language Config
          </Button>
        </Box>

        <LanguageConfigTable
          configs={configs}
          loading={loading}
          error={error}
          unauthorized={unauthorized}
          busyId={busyId}
          onToggle={handleToggle}
          onEdit={openEdit}
          onDelete={handleDelete}
        />
      </Box>

      <LanguageConfigModal
        open={modalOpen}
        editingConfig={editingConfig}
        languages={languages}
        usedLanguageCodes={usedLanguageCodes}
        submitting={submitting}
        formError={formError}
        onClose={closeModal}
        onSubmit={handleSubmit}
      />
    </Layout>
  );
};

export default LanguageConfig;
